import React, { useEffect, useRef, useCallback, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import {
    saveDonationReturnPath,
    markDonationPremiumGranted,
} from './donationReturnPath';
import { dismissPaypalDonatePopupSoon } from './paypalDonatePopup';
import {
    storeLastDonation,
    trackDonationComplete,
    trackEvent,
} from './analytics';

const DONATE_SDK_SRC = 'https://www.paypalobjects.com/donate/sdk/donate-sdk.js';

const DEFAULT_HOSTED_BUTTON_ID = 'ZT9F4FKHWZ6F2';

async function ensureDonateSdkScript(timeoutMs = 10000) {
    if (typeof window === 'undefined') throw new Error('no window');
    if (!document.querySelector(`script[src="${DONATE_SDK_SRC}"]`)) {
        await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = DONATE_SDK_SRC;
            s.charset = 'UTF-8';
            s.async = true;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error('PayPal script load failed'));
            document.body.appendChild(s);
        });
    }
    const t0 = Date.now();
    while (!getDonateButtonApi()) {
        if (Date.now() - t0 > timeoutMs) throw new Error('PayPal Donate SDK timeout');
        await new Promise((r) => setTimeout(r, 50));
    }
}

function getDonateButtonApi() {
    return window.PayPal?.Donation?.Button || window.Donation?.Button;
}

function hostedButtonId() {
    const raw = (typeof process !== 'undefined' && process.env?.REACT_APP_PAYPAL_HOSTED_BUTTON_ID) || '';
    const trimmed = String(raw).trim();
    return trimmed || DEFAULT_HOSTED_BUTTON_ID;
}

function paypalEnv() {
    const v = (typeof process !== 'undefined' && process.env?.REACT_APP_PAYPAL_DONATION_ENV) || '';
    return String(v).toLowerCase() === 'sandbox' ? 'sandbox' : 'production';
}

function getStoredAuthToken() {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('authToken');
}

function usernameFromToken(token) {
    if (!token) return null;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.username || null;
    } catch {
        return null;
    }
}

/** Stretch PayPal SDK nodes to cover the visual button (transparent hit target). */
function stretchPaypalHitTarget(root) {
    if (!root) return;
    root.querySelectorAll('a, button, img, [role="button"]').forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        node.style.position = 'absolute';
        node.style.inset = '0';
        node.style.width = '100%';
        node.style.height = '100%';
        node.style.margin = '0';
        node.style.opacity = '0';
        node.style.cursor = 'pointer';
    });
}

/**
 * Yellow Bootstrap label + transparent PayPal SDK layer on top (real clicks).
 */
export default function PayPalDonateSdkButton({
    authToken,
    getHost,
    className = '',
    compact = false,
    active = true,
    inModal = false,
    donationSource = 'unknown',
    requireAuthForComplete = false,
    fallbackCustomFromJwt = false,
    returnPathFromCurrentPage = false,
    onPremiumGranted,
    onError,
}) {
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const reactDomId = useId().replace(/:/g, '');
    const [loadError, setLoadError] = useState(false);

    const handleComplete = useCallback(
        async (params) => {
            dismissPaypalDonatePopupSoon();

            const txnId = (params?.tx || '').trim();
            const status = (params?.st || '').trim();
            const amount = (params?.amt || '').trim();
            const currency = (params?.cc || '').trim();
            let custom = (params?.cm || '').trim();

            const effectiveToken = requireAuthForComplete
                ? (getStoredAuthToken() || authToken)
                : authToken;

            if (!effectiveToken) {
                dismissPaypalDonatePopupSoon();
                if (requireAuthForComplete) {
                    window.alert(
                        'Παρακαλούμε συνδεθείτε για να ενεργοποιηθεί η premium πρόσβαση.'
                    );
                } else {
                    window.alert('Ευχαριστούμε πολύ για τη στήριξή σας!');
                }
                return;
            }

            if (!custom && fallbackCustomFromJwt) {
                const username = usernameFromToken(effectiveToken);
                if (username) {
                    custom = `username:${username}`;
                }
            }

            if (!txnId) {
                window.alert('Η PayPal δεν επέστρεψε αναγνωριστικό συναλλαγής.');
                onError?.();
                return;
            }

            try {
                const res = await fetch(`${getHost()}/api/donation-paypal-complete`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${effectiveToken}`,
                    },
                    body: JSON.stringify({
                        txn_id: txnId,
                        status,
                        amount,
                        currency,
                        custom,
                    }),
                });
                const body = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(body.message || 'Αποτυχία επιβεβαίωσης δωρεάς');
                }
                if (body.token) localStorage.setItem('authToken', body.token);
                if (body.refresh_token) localStorage.setItem('refreshToken', body.refresh_token);
                markDonationPremiumGranted();
                storeLastDonation({
                    transactionId: txnId,
                    premiumGranted: true,
                    donationSource,
                });
                trackDonationComplete({
                    transactionId: txnId,
                    premiumGranted: true,
                    donationSource,
                });
                onPremiumGranted?.();
                dismissPaypalDonatePopupSoon();
                navigate('/donation-success', { replace: true });
            } catch (e) {
                dismissPaypalDonatePopupSoon();
                window.alert(e instanceof Error ? e.message : 'Σφάλμα');
                onError?.();
            }
        },
        [
            authToken,
            donationSource,
            fallbackCustomFromJwt,
            getHost,
            navigate,
            onPremiumGranted,
            onError,
            requireAuthForComplete,
        ]
    );

    useEffect(() => {
        if (!active) return undefined;
        const el = containerRef.current;
        if (!el) return undefined;

        const onClick = () => {
            trackEvent('donate_click', { donation_source: donationSource });
        };
        el.addEventListener('click', onClick, true);
        return () => el.removeEventListener('click', onClick, true);
    }, [active, donationSource]);

    useEffect(() => {
        if (!active) return undefined;

        const el = containerRef.current;
        if (!el) return undefined;

        let cancelled = false;
        setLoadError(false);
        if (returnPathFromCurrentPage && typeof window !== 'undefined') {
            saveDonationReturnPath(`${window.location.pathname}${window.location.search}`);
        } else {
            saveDonationReturnPath('/predict');
        }

        const mountDelay = inModal ? 150 : 0;

        const timer = window.setTimeout(() => {
            (async () => {
                try {
                    await ensureDonateSdkScript();
                } catch {
                    if (!cancelled) setLoadError(true);
                    return;
                }
                if (cancelled || !containerRef.current) return;

                const Btn = getDonateButtonApi();
                if (!Btn) {
                    if (!cancelled) setLoadError(true);
                    return;
                }

                containerRef.current.innerHTML = '';

                const username = usernameFromToken(authToken);

                const cfg = {
                    env: paypalEnv(),
                    hosted_button_id: hostedButtonId(),
                    image: {
                        src: 'https://www.paypalobjects.com/webstatic/icon/pp258.png',
                        title: 'PayPal — Donate',
                        alt: 'Donate',
                    },
                    onComplete: (p) => {
                        handleComplete(p);
                    },
                };
                if (username) {
                    cfg.custom = `username:${username}`;
                }

                const mountSelector = `#${CSS.escape(reactDomId)}`;
                Btn(cfg).render(mountSelector);
                requestAnimationFrame(() => {
                    if (!cancelled) stretchPaypalHitTarget(containerRef.current);
                });
            })();
        }, mountDelay);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            if (el) el.innerHTML = '';
        };
    }, [active, authToken, handleComplete, inModal, reactDomId, returnPathFromCurrentPage]);

    const wrapClass = [
        'paypal-donate-trigger-wrap',
        compact ? 'paypal-donate-trigger-wrap--compact' : '',
        inModal ? 'paypal-donate-trigger-wrap--modal' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    if (!active) {
        return null;
    }

    return (
        <span className={wrapClass}>
            <Button
                type="button"
                variant="warning"
                size={compact ? 'sm' : undefined}
                className="paypal-donate-btn-visual"
                tabIndex={-1}
                aria-hidden="true"
            >
                <img
                    src="https://www.paypalobjects.com/webstatic/icon/pp258.png"
                    alt=""
                    className="paypal-donate-btn-icon"
                />
                Donate
            </Button>
            <span
                id={reactDomId}
                className="paypal-donate-mount paypal-donate-mount--hitlayer"
                ref={containerRef}
                aria-label="Donate with PayPal"
            />
            {loadError && (
                <span className="paypal-donate-load-error small text-danger">
                    Δεν φόρτωσε το PayPal. Ανανεώστε τη σελίδα.
                </span>
            )}
        </span>
    );
}
